import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get, isNil } from 'lodash';
import moment from 'moment';
import cn from 'classnames';
import Datetime from 'react-datetime';
import CardNumberInput from '@lendingusa/react-common-card-number-input';

import {
  newNextAction,
  validateBankRoutingNumberAction,
} from '../../actions/navigation';

import Transition from '../../components/Transition';
import Modal from '../../components/Modal';
import Popup from '../../components/Popup';
import Button from '../../components/Button';
import VerifyButton from '../../components/Button/VerifyButton';
import Header from '../../components/Header';
import Progress from '../../components/Progress';
import FormRow from '../../components/Form/FormRow';
import FormColumn from '../../components/Form/FormColumn';
import FormGroup from '../../components/Form/FormGroup';
import ApplicationId from '../../components/ApplicationId';
import ContentWrapper from '../../components/ContentWrapper';
import Validator from '../../components/Validator';
import Input from '../../components/Form/Input';
import Select from '../../components/Form/Select';
import imgError from '../../assets/images/error.svg';

import { getOrConvertToPst } from '../../utils/timezone';
import { verifyAddress } from '../../utils/verifyAddress';
import { parseUrlParams, addLusaHeaderParam } from '../../utils/parseUrlParams';
import { unsupportedStates } from '../../utils/unsupportedStates';
import { accountMask, routingNumberMask, numberUnmask } from '../../utils/masks';
import { toTitleCase } from '../../utils/toTitleCase';
import { states } from '../../utils/states';
import editIcon from '../../assets/images/edit.png';
import cancelEditIcon from '../../assets/images/cancel-edit.png';
import VerifiedIcon from '../../assets/images/correct.png';
import WarningIcon from '../../assets/images/warning.png';
import NoAchIcon from '../../assets/icons/debit_card.svg';
import XIcon from '../../assets/icons/x.svg';

import schema from './achSchema';
import './style.scss';

const INITIAL_VALIDATION_STATE = {
  loading: false,
  verified: false,
  checkedFirstTime: false,
  data: null,
};

const LuwaACH = (props) => {
  const { validator: { validate, values, setValues, errors }, checkinData, navigate, location } = props;
  const match = useParams();
  const workflowType = get(match, 'workflowType') === 'luwa' ? 'dtm-luwa' : get(match, 'workflowType');
  const isAutoPayOffer = get(checkinData, 'data.data.isAutoPayOffer', get(checkinData, 'state.data.isAutoPayOffer', ''));
  const zipTerm = get(checkinData, 'data.data.zipTerm', get(checkinData, 'state.data.zipTerm'));
  const originationPercentage = get(checkinData, 'data.data.originationPercentage') || get(checkinData, 'data.data.originationPercentage');
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInputEmpty, setIsInputEmpty] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAddressPopupVisible, setIsAddressPopupVisible] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [popupType, setPopupType] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [prevAddresses, setPrevAddresses] = useState({});
  const [isDatePickVisible, setIsDatePickVisible] = useState(false);
  const [isGiactPopupVisible, setIsGiactPopupVisible] = useState(false);
  const [giactPopupAttrs, setGiactPopupAttrs] = useState(null);
  const [giactMessage, setGiactMessage] = useState(null);
  const [firstPaymentDate, setFirstPaymentDate] = useState(getOrConvertToPst().add(37, 'd'));
  const [uploadingPercentage, setUploadingPercentage] = useState(0);
  const [isNoAutopayPopupVisible, setIsNoAutopayPopupVisible] = useState(false);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [routingValidateStatus, setRoutingValidateStatus] = useState(INITIAL_VALIDATION_STATE);
  const paymentData = useRef({
    isBankAccountExisting: false,
    isDebitCardExisting: false,
  });
  const noMatchingOffer = !isAutoPayOffer && isNil(zipTerm);

  useEffect(() => {
    if (isInputEmpty) {
      const elementHasError = document.getElementsByClassName('error');
      const elementIsReq = document.getElementsByClassName('required');
      const elements = [...elementHasError, ...elementIsReq];
      if (elements && elements.length > 0) {
        const scrollPosition = elements[0].getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        setIsInputEmpty(false);
      }
    }
  }, [isInputEmpty]);

  useEffect(() => {
    const params = parseUrlParams(window.location.search);

    const sourceData = get(checkinData, ['data', 'data']) || get(checkinData, ['state', 'data']);
    if (!sourceData) {
      setTimeout(() => navigate(addLusaHeaderParam(`/${workflowType === 'dtm-luwa' ? 'luwa' : workflowType}/results/continue/?key=${params.key}`)), 0);
    } else {
      const dataObj = get(checkinData, 'data.data') || get(checkinData, 'state.data');
      const accounts = get(dataObj, 'bankAccounts', undefined);
      const primaryAccountIndex = (accounts || []).findIndex((acc) => acc.isPrimary);
      const payFromAccount = accounts ? accounts[primaryAccountIndex === -1 ? 0 : primaryAccountIndex] : undefined;
      const debitCard = get(dataObj, 'debitCard');
      const error = get(dataObj, 'errors.0') || get(checkinData, 'failure.errors.0', '');

      if (error) {
        setHasError(true);
        setErrorMessage(get(error, 'message'));
      }

      let initialBankData = {
        accounts,
        payFromAccount: JSON.stringify(payFromAccount),
        firstPaymentDate: getOrConvertToPst().add(37, 'd'),
        isEditingDebitCard: false,
        isEditingBankInfo: false,
        debitCard: {
          accountHolderName: `${get(dataObj, 'applicant.firstName', '') || ''} ${get(dataObj, 'applicant.lastName', '') || ''}`,
        },
      };

      if (payFromAccount) {
        initialBankData = {
          ...initialBankData,
          accountId: get(payFromAccount, 'accountId', ''),
          bankAccount: {
            accountNumber: get(payFromAccount, 'accountNumber', '') || '',
            routingNumber: get(payFromAccount, 'routingNumber', '') || '',
            accountType: get(payFromAccount, 'accountType', 'Checking') || 'Checking',
            accountHolderName: get(payFromAccount, 'accountHolderName', '') || '',
            bankName: get(payFromAccount, 'bankName', '') || '',
          },
          isBankAccountExisting: true,
          isValidRoutingNumber: true,
        };
        paymentData.current.isBankAccountExisting = true;
        setRoutingValidateStatus({
          ...routingValidateStatus,
          verified: true,
          checkedFirstTime: true,
        });
      } else {
        initialBankData.isBankAccountExisting = false;
        paymentData.current.isBankAccountExisting = false;
      }

      if (debitCard) {
        const expYear = get(debitCard, 'expirationDate.year', 0);
        const expMonth = get(debitCard, 'expirationDate.month', 0);

        initialBankData = {
          ...initialBankData,
          debitCard: {
            ...initialBankData.debitCard,
            cardNumber: get(debitCard, 'cardNumberLast4', '') || '',
            expirationDate: moment(`${expMonth}/${expYear}`, 'MM/YYYY').format('MM/YY'),
          },
          isDebitCardExisting: true,
        };
        paymentData.current.isDebitCardExisting = true;
      } else {
        initialBankData.isDebitCardExisting = false;
        paymentData.current.isDebitCardExisting = false;
      }

      if (get(dataObj, 'isAutoPay')) {
        initialBankData.isSelfSubmitted = get(dataObj, 'isDebitCard') ? 1 : 2;
      } else {
        initialBankData.isSelfSubmitted = isAutoPayOffer ? 1 : 2;
      }

      setValues(initialBankData);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const validateRoutingNo = (routingNumber) => {
    const newValue = routingNumber.replace(/[^\d]/g, '');
    if (newValue.length === 9) {
      return true;
    }

    if (routingValidateStatus.checkedFirstTime) {
      setRoutingValidateStatus({ ...routingValidateStatus, verified: false });
    }
    return false;
  };

  const validateRoutingNumber = (routingNo) => {
    const { validator: { deleteErrors, setErrors } } = props;
    let routingNumberUnMask;
    if (routingNo && routingNo.length === 9) {
      routingNumberUnMask = routingNo.replace(/[^\d]/g, '');
    }
    if (!routingNumberUnMask || routingNumberUnMask.length !== 9) return;
    setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, loading: true, data: null });
    if (routingNo) {
      props.validateBankRoutingNumberAction({
        routingNumber: routingNo,
        workflowType,
        success: (res) => {
          const validateRespose = res && res.length > 0 ? res[0] : null;
          const validateStatus = validateRespose && validateRespose.status;
          const validateData = validateRespose && validateRespose.data;

          if (validateStatus && validateStatus === 'success') {
            setValues({
              ...values,
              bankAccount: {
                ...values.bankAccount,
                bankName: validateData.name,
                routingNumber: routingNo,
              },
              isValidRoutingNumber: true,
            });
            deleteErrors(['bankAccount.routingNumber']);
            setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: true, loading: false, data: validateData });
          }
          if (validateStatus && validateStatus === 'fail') {
            setErrors({ ...errors, 'bankAccount.routingNumber': validateData.message || 'An error occurred' });
            setValues({
              ...values,
              bankAccount: {
                ...values.bankAccount,
                bankName: '',
                routingNumber: routingNo,
              },
              isValidRoutingNumber: false,
            });
            setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: false, loading: false, data: null });
          }
        },
        fail: () => {
          setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: false, loading: false, data: null });
        },
      });
    }
  };

  const verifySmartyStreet = ({ address, city, state, zipcode }) => {
    setIsAddressLoading(true);

    verifyAddress({ zipcode, address, city, state }).then((response) => {
      if (response) {
        if (get(response, 'metadata.recordType') === 'P') {
          setIsAddressPopupVisible(true);
          setIsAddressLoading(false);
          setIsVerified(false);
          setPopupType(3);
          setIsFirstLoad(false);
        } else {
          const addressData = {
            ...values,
            owner: {
              line1: response.deliveryLine1,
              city: get(response, 'components.cityName'),
              state: get(response, 'components.state'),
              zipcode: `${response.deliveryPointBarcode}`.slice(0, 5),
            },
          };
          const hasOptional = `${get(response, 'analysis.footnotes', '')}`.indexOf('H#') !== -1;
          setIsVerified(true);
          setIsAddressLoading(false);
          setIsFirstLoad(false);
          setPrevAddresses({ line1: address, city, state, zipcode });
          setPopupType(hasOptional ? 5 : null);
          setIsAddressPopupVisible(hasOptional);
          setValues(addressData);
        }
      } else {
        setIsAddressPopupVisible(true);
        setIsAddressLoading(false);
        setIsFirstLoad(false);
        setIsVerified(false);
        setPopupType(2);
      }
    }).catch(() => {
      setIsAddressLoading(false);
      setPopupType(1);
      setIsFirstLoad(false);
      setIsVerified(false);
    });
  };

  const handleVerifyAddressClick = (e) => {
    e.preventDefault();

    if (!get(values, 'owner.line1')) {
      setIsAddressPopupVisible(true);
      setPopupType(1);
    } else {
      verifySmartyStreet({
        address: get(values, 'owner.line1'),
        city: get(values, 'owner.city'),
        state: get(values, 'owner.state'),
        zipcode: get(values, 'owner.zipcode'),
      });
    }
  };

  const handleBlur = (event) => {
    event.preventDefault();
    const { validator: { onChangeHandler } } = props;

    switch (event.target.name) {
      case 'owner.state':
        if (
          !(event.target.value === 'IA') && unsupportedStates.indexOf(event.target.value) !== -1
        ) {
          setIsAddressPopupVisible(true);
          setIsAddressLoading(false);
          setPopupType(4);
        } else if (isFirstLoad && get(values, 'owner.city') && get(values, 'owner.line1')) {
          verifySmartyStreet({
            zipcode: get(values, 'owner.zipcode'),
            address: get(values, 'owner.line1'),
            city: get(values, 'owner.city'),
            state: event.target.value,
          });
        }
        break;
      case 'owner.zipcode':
        if (isFirstLoad && get(values, 'owner.city') && get(values, 'owner.state') && get(values, 'owner.line1')) {
          verifySmartyStreet({
            zipcode: event.target.value,
            address: get(values, 'owner.address'),
            city: get(values, 'owner.city'),
            state: get(values, 'owner.state'),
          });
        }
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'owner.line1':
        if (isFirstLoad && get(values, 'owner.city') && get(values, 'owner.state')) {
          verifySmartyStreet({
            zipcode: get(values, 'owner.zipcode'),
            address: toTitleCase(event.target.value),
            city: get(values, 'owner.city'),
            state: get(values, 'owner.state'),
          });
        }
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'owner.city':
        if (isFirstLoad && get(values, 'owner.address') && get(values, 'owner.state')) {
          verifySmartyStreet({
            zipcode: get(values, 'owner.zipcode'),
            address: get(values, 'owner.line1'),
            city: toTitleCase(event.target.value),
            state: get(values, 'owner.state'),
          });
        }
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      default:
    }
  };

  const handleSubmitForm = (flag = false, e) => {
    e.preventDefault();

    const params = parseUrlParams(window.location.search);
    if (params.key) {
      setHasError(false);
      setErrorMessage();

      if (schema && validate(schema).isValid) {
        setIsProgressVisible(true);

        const datePattern = 'MM/DD/YYYY';

        let payloadData = {
          isAutoPay: values.isSelfSubmitted === 1 || values.isSelfSubmitted === 2,
          firstPaymentDate: moment(values.firstPaymentDate).format(datePattern),
          isDebitCard: values.isSelfSubmitted === 1,
          iframe: params.iframe,
        };

        if (flag) {
          payloadData = {
            ...payloadData,
            isDebitCard: false,
            isAutoPay: false,
          };
        }

        if ((!paymentData.current.isDebitCardExisting || values.isEditingDebitCard) && values.isSelfSubmitted === 1) {
          const dateSplit = get(values, 'debitCard.expirationDate').split('/');
          const normalizedDate = `${dateSplit[0]}/20${dateSplit[1]}`;
          const expirationDateMoment = moment(normalizedDate, 'MM/YYYY');
          payloadData.DebitCard = {
            AccountHolderName: get(values, 'debitCard.accountHolderName', ''),
            CardNumber: numberUnmask(get(values, 'debitCard.cardNumber', '')),
            Expiration: {
              Month: expirationDateMoment.month() + 1,
              Year: expirationDateMoment.year(),
            },
          };
        }

        if (values.isSelfSubmitted === 2 && !flag) {
          if (!paymentData.current.isBankAccountExisting || values.isEditingBankInfo) {
            payloadData.BankAccount = {
              AccountNumber: get(values, 'bankAccount.accountNumber', ''),
              RoutingNumber: get(values, 'bankAccount.routingNumber', ''),
              AccountType: get(values, 'bankAccount.accountType', ''),
              AccountHolderName: get(values, 'bankAccount.accountHolderName', ''),
              BankName: get(values, 'bankAccount.bankName', ''),
            };
          } else {
            payloadData.accountId = values.accountId;
          }
        }

        if (values.avsFailed) {
          payloadData.DebitCard = {
            ...(payloadData.DebitCard || {}),
            owner: {
              ...values.owner,
            },
          };
        }

        setHasError(false);
        setErrorMessage();
        setValues({ ...values });
        props.newNextAction({
          key: params.key,
          workflowType,
          data: payloadData,
          onUploadProgress: (progressEvent) => {
            setUploadingPercentage(
              Math.floor((progressEvent.loaded * 100) / progressEvent.total)
            );
          },
          success: (res) => {
            let routeUrl = get(res, 'data.url');
            if (routeUrl && params.lusa_header && params.lusa_header === 'false') {
              routeUrl = addLusaHeaderParam(routeUrl);
            }
            navigate(routeUrl);
          },
          fail: (err) => {
            setIsProgressVisible(false);

            if (get(err, 'response.data.failure.errors.0.subject') === 'bankAccountVerificationFailed') {
              setIsGiactPopupVisible(true);
              setGiactPopupAttrs(get(err, 'response.data.failure.errors.0.attributes'));
              setGiactMessage(get(err, 'response.data.failure.errors.0.message'));
            } else {
              setHasError(true);

              if (get(err, 'response.data.failure.errors.0')) {
                setErrorMessage(get(err, 'response.data.failure.errors.0.message'));

                if (get(err, 'response.data.failure.errors.0.subject') === 'avsFailed') {
                  setValues({ ...values, avsFailed: true });
                }
              } else {
                setErrorMessage('An unexpected error happened while saving the payment information. Please try again later.');
              }
            }
          },
        });
      } else {
        validate(schema);
        setIsInputEmpty(true);
      }
    }
  };

  const prefillBankAccount = (accountIndex) => {
    const accounts = get(values, 'accounts', []);
    const bankData = accounts.find((acc) => acc.accountId === accountIndex);

    if (bankData) {
      setValues({
        ...values,
        accountId: get(bankData, 'accountId'),
        payFromAccount: JSON.stringify(bankData),
        bankAccount: {
          accountHolderName: get(bankData, 'accountHolderName') || '',
          bankName: get(bankData, 'bankName') || '',
          routingNumber: get(bankData, 'routingNumber') || '',
          accountNumber: get(bankData, 'accountNumber') || '',
          accountType: get(bankData, 'accountType') || '',
        },
      });
    }
  };

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    switch (event.target.name) {
      case 'accountId':
        prefillBankAccount(event.target.value);
        break;
      case 'bankAccount.routingNumber':
        if (!routingValidateStatus.loading) {
          onChangeHandler(event.target.name, event.target.value);
          if (validateRoutingNo(event.target.value) && event.target.value !== values.routingNumber) {
            validateRoutingNumber(event.target.value);
          }
        }
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
    }
  };

  const toggleGiactPopup = useCallback((e) => {
    e.preventDefault();

    setIsGiactPopupVisible(false);
  }, []);

  const handleConfirmDate = () => {
    const { validator: { onChangeHandler } } = props;

    setIsDatePickVisible(false);
    onChangeHandler('firstPaymentDate', firstPaymentDate);
  };

  const handleDateChange = (date) => {
    setFirstPaymentDate(date);
  };

  const toggleDebitCardEdit = () => {
    const dataObj = get(checkinData, 'data.data', {}) || get(checkinData, 'state.data');
    const debitCardData = get(dataObj, 'debitCard', null);

    if (!values.isEditingDebitCard) {
      setValues({
        ...values,
        debitCard: {
          ...values.debitCard,
          cardNumber: '',
          expirationDate: '',
        },
        isEditingDebitCard: !values.isEditingDebitCard,
      });
    } else {
      const expYear = get(debitCardData, 'expirationDate.year', 0);
      const expMonth = get(debitCardData, 'expirationDate.month', 0);

      setValues({
        ...values,
        debitCard: {
          ...values.debitCard,
          cardNumber: get(debitCardData, 'cardNumberLast4', ''),
          expirationDate: moment(`${expMonth}/${expYear}`, 'MM/YYYY').format('MM/YY'),
        },
        isEditingDebitCard: !values.isEditingDebitCard,
      });
    }
  };

  const toggleBankInfoEdit = () => {
    const { validator: { deleteErrors } } = props;
    const payFromAccount = values.payFromAccount ? JSON.parse(values.payFromAccount) : null;

    if (!values.isEditingBankInfo) {
      setValues({
        ...values,
        bankAccount: {
          accountNumber: '',
          routingNumber: '',
          accountType: 'Checking',
          accountHolderName: '',
          bankName: '',
        },
        isEditingBankInfo: !values.isEditingBankInfo,
        isValidRoutingNumber: false,
      });
      setRoutingValidateStatus(INITIAL_VALIDATION_STATE);
    } else {
      setValues({
        ...values,
        bankAccount: {
          accountNumber: get(payFromAccount, 'accountNumber', ''),
          routingNumber: get(payFromAccount, 'routingNumber', ''),
          accountType: get(payFromAccount, 'accountType', 'Checking'),
          accountHolderName: get(payFromAccount, 'accountHolderName', ''),
          bankName: get(payFromAccount, 'bankName', ''),
        },
        isEditingBankInfo: !values.isEditingBankInfo,
        isValidRoutingNumber: true,
      });
      setRoutingValidateStatus({
        ...routingValidateStatus,
        verified: true,
        checkedFirstTime: true,
      });
      deleteErrors(['bankAccount.routingNumber', 'bankAccount.accountHolderName', 'bankAccount.accountNumber']);
    }
  };

  const handleCheckboxChange = (name, value) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(name, value);

    if (name === 'isSelfSubmitted' && value === 2 && !values.bankAccount) {
      onChangeHandler('bankAccount.accountType', 'Checking');
      setValues({
        ...values,
        isSelfSubmitted: value,
        bankAccount: {
          ...(values.bankAccount || {}),
        },
      });
    } else if (name === 'isSelfSubmitted' && value === 3) {
      setIsNoAutopayPopupVisible(true);
      onChangeHandler(name, value);
    } else {
      onChangeHandler(name, value);
      if (value !== 2) {
        // setIsDownPaymentOpened(false);
        // setIsAutopayElectionOpened(false);
        // setIsPickPaymentDateOpened(true);
      }
    }
  };

  const handleFocus = () => {
    setIsDatePickVisible(true);
  };

  const toggleNoAutopayPopup = (e) => {
    e.preventDefault();
    setIsNoAutopayPopupVisible(!isNoAutopayPopupVisible);
  };

  const handleUseDebitCard = (e) => {
    const { validator: { onChangeHandler } } = props;
    e.preventDefault();
    onChangeHandler('isSelfSubmitted', 1);
    setIsNoAutopayPopupVisible(false);
  };

  const handleCloseModal = () => {
    setIsDatePickVisible(false);
    setFirstPaymentDate(values.firstPaymentDate);
  };

  const handleUndo = (e) => {
    e.preventDefault();

    const addressData = {
      ...values,
      owner: {
        line1: get(prevAddresses, 'prevAddresses.line1'),
        city: get(prevAddresses, 'prevAddresses.city'),
        state: get(prevAddresses, 'prevAddresses.state'),
        zipcode: get(prevAddresses, 'prevAddresses.zipcode'),
      },
    };

    setIsVerified(false);
    setValues(addressData);
  };

  const handleAbort = () => {
    setIsAddressPopupVisible(false);
  };

  const getCardSubtitle = () => {
    let subtitle;
    if (zipTerm >= 1 && originationPercentage === 0) {
      subtitle = `0% APR if paid off within ${zipTerm} months*`;
    } else if (zipTerm >= 1 && originationPercentage > 0) {
      subtitle = `NO INTEREST if paid off within ${zipTerm} months*`;
    }
    return subtitle;
  };

  const renderMethodOption = (optionIndex) => (
    <div className={cn('method-option-item text-center', values.isSelfSubmitted === optionIndex && 'selected')} onClick={handleCheckboxChange.bind(null, 'isSelfSubmitted', optionIndex)}>
      {optionIndex === 2 ? (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M3 8v12h18V8M3 8l9-4 9 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M2 10h20" stroke="currentColor" strokeWidth="2" />
        </svg>
      )}
      <h4>{optionIndex === 2 ? 'Bank Account' : 'Debit Card'}</h4>
      <p>{getCardSubtitle()}</p>
    </div>
  );

  const { isDebitCardExisting, isBankAccountExisting } = paymentData.current;

  const [cardError, setCardError] = useState('');

  const handleCardChange = (digits) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler('debitCard.cardNumber', digits);
  };

  const handleCardComplete = (digits, isValid) => {
    const { validator: { deleteErrors, setErrors } } = props;
    if (!isValid) {
      setErrors({ ...errors, 'debitCard.cardNumber': 'Invalid card number' });
    } else {
      deleteErrors(['debitCard.cardNumber']);
    }
  };

  const handleCardBlur = (digits, isValid) => {
    const { validator: { setErrors } } = props;
    if (digits && !isValid) {
      setErrors({ ...errors, 'debitCard.cardNumber': 'Invalid card number' });
    }
  };

  const handleCardBrandChange = (brand, isAllowed) => {
    if (brand && !isAllowed) {
      setCardError('Currently we accept only Discover, Visa or Mastercard');
    } else {
      setCardError('');
    }
  };

  const handleCardCancel = () => {
    toggleDebitCardEdit();
  };

  const handleCardRevert = () => {
    toggleDebitCardEdit();
  };

  return (
    <div className="debit-card-election dponly-payment-page autopay-election-page">
      <Transition className="transition-item wrapper v-center">
        <Header />
        {get(checkinData, 'attributes.entityId') ? (
          <ApplicationId id={get(checkinData, 'attributes.entityId')} style={{ marginTop: 10 }} />
        ) : null}
        <hr className="separator pt-20" />
        <ContentWrapper className="debit-card-election-content">
          <div className="content-section w-100">
            <div className="enroll-section">
              <FormRow className="mb-25">
                <FormColumn>
                  <h2 className="title-header">
                    {noMatchingOffer ? 'Never Miss a Payment' : 'Save Money with AutoPay'}
                  </h2>
                  <p className="autopay-subtitle">
                    {noMatchingOffer
                      ? 'Turn on autopay and let your payments run on autopilot.'
                      : !isAutoPayOffer
                        ? 'Wait! I changed my mind and want to SAVE MONEY with the NO INTEREST PROMOTION by enrolling in AutoPay! (Your loan terms will be updated automatically with the best available terms if you sign up for AutoPay)'
                        : 'Choose your preferred payment method to enroll in AutoPay.'}
                  </p>
                </FormColumn>
              </FormRow>
              <FormRow className="mb-1">
                <FormColumn>
                  {renderMethodOption(2)}
                </FormColumn>
                <FormColumn>
                  {renderMethodOption(1)}
                </FormColumn>
              </FormRow>
              {!isAutoPayOffer && (
                <FormRow>
                  <FormColumn>
                    <button
                      type="button"
                      className={`noAutoPayBtn ${values.isSelfSubmitted === 3 ? 'selected' : ''}`}
                      onClick={handleCheckboxChange.bind(null, 'isSelfSubmitted', 3)}
                    >
                      <span>×</span>
                      Continue without AutoPay promotion
                    </button>
                  </FormColumn>
                </FormRow>
              )}
              {errors.isSelfSubmitted ? (
                <FormRow>
                  <FormColumn>
                    <span className="error">{errors.isSelfSubmitted}</span>
                  </FormColumn>
                </FormRow>
              ) : null}
            </div>
            <div className="form-box">
              {
                values.isSelfSubmitted === 2 && (
                  <>
                    {isBankAccountExisting && (
                      <div className="edit-container" onClick={toggleBankInfoEdit}>
                        <img src={values.isEditingBankInfo ? cancelEditIcon : editIcon} alt={values.isEditingDebitCard ? 'edit icon' : 'cancel edit icon'} />
                      </div>
                    )}
                    {values.accounts && values.accounts.length > 1 && !values.isEditingBankInfo ? (
                      <FormRow>
                        <FormColumn>
                          <Select
                            data={values.accounts.map((acc) => ({
                              value: acc.accountId, title: `${acc.accountType} #${acc.accountNumber.slice(-4)}`,
                            }))}
                            label="Pay from Account"
                            placeholder="Select..."
                            name="accountId"
                            onChange={handleInputChange}
                            isRequired
                            isErrorVisible={false}
                            value={get(values, 'accountId', '')}
                          />
                        </FormColumn>
                      </FormRow>
                    ) : null}
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Input
                            label="Name on Account"
                            name="bankAccount.accountHolderName"
                            value={get(values, 'bankAccount.accountHolderName', '') || ''}
                            onChange={handleInputChange}
                            hasError={!!get(errors, 'bankAccount.accountHolderName')}
                            errorMessage={get(errors, 'bankAccount.accountHolderName')}
                            readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Select
                            label="Account Type"
                            name="bankAccount.accountType"
                            data={[
                              { value: 'Checking', title: 'Checking' },
                              { value: 'Savings', title: 'Savings' },
                            ]}
                            value={get(values, 'bankAccount.accountType', '')}
                            onChange={handleInputChange}
                            hasError={!!get(errors, 'bankAccount.accountType')}
                            errorMessage={get(errors, 'bankAccount.accountType')}
                            isDisabled={!values.isEditingBankInfo && isBankAccountExisting}
                            className="readOnly"
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <div className="position-relative">
                            <Input
                              type="tel"
                              label="Routing Number"
                              name="bankAccount.routingNumber"
                              value={get(values, 'bankAccount.routingNumber', '') || ''}
                              onChange={handleInputChange}
                              isMasked={(values.isEditingBankInfo || !isBankAccountExisting) ? routingNumberMask : undefined}
                              hasError={!!get(errors, 'bankAccount.routingNumber')}
                              errorMessage={get(errors, 'bankAccount.routingNumber')}
                              readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                              isRequired
                            />
                            {get(values, 'bankAccount.bankName') && routingValidateStatus.verified ? (
                              <div className="bankName">
                                <span>{get(values, 'bankAccount.bankName')}</span>
                              </div>
                            ) : null}
                            {routingValidateStatus.loading ? <span className="input-loader" /> : null}
                            {!routingValidateStatus.loading && routingValidateStatus.checkedFirstTime ? (
                              <span className="input-status">
                                <img
                                  src={`${routingValidateStatus.verified ? VerifiedIcon : WarningIcon}`}
                                  alt="input-status"
                                />
                              </span>
                            ) : null}
                          </div>
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Input
                            type="tel"
                            label="Account Number"
                            name="bankAccount.accountNumber"
                            value={get(values, 'bankAccount.accountNumber', '') || ''}
                            onChange={handleInputChange}
                            isMasked={(values.isEditingBankInfo || !isBankAccountExisting) ? accountMask : undefined}
                            hasError={!!get(errors, 'bankAccount.accountNumber')}
                            errorMessage={get(errors, 'bankAccount.accountNumber')}
                            readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                  </>
                )
              }
              {
                values.isSelfSubmitted === 1 && (
                  <>
                    {isDebitCardExisting && (
                      <div className="edit-container" onClick={toggleDebitCardEdit}>
                        <img src={values.isEditingDebitCard ? cancelEditIcon : editIcon} alt={values.isEditingDebitCard ? 'edit icon' : 'cancel edit icon'} />
                      </div>
                    )}
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Input
                            label="Name"
                            name="debitCard.accountHolderName"
                            value={get(values, 'debitCard.accountHolderName', '') || ''}
                            onChange={handleInputChange}
                            hasError={!!get(errors, 'debitCard.accountHolderName')}
                            errorMessage={get(errors, 'debitCard.accountHolderName')}
                            isRequired
                            readOnly
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow className="debit-card-container">
                      <FormColumn offset={12} className="inputWrapper">
                        <CardNumberInput
                          className="card-number-input"
                          value={(!isDebitCardExisting || values.isEditingDebitCard) ? (get(values, 'debitCard.cardNumber') || null) : null}
                          lastFour={isDebitCardExisting && !values.isEditingDebitCard ? (get(values, 'debitCard.cardNumber') || null) : null}
                          allowedBrands={['visa', 'mastercard', 'discover']}
                          onChange={handleCardChange}
                          onComplete={handleCardComplete}
                          onBlur={handleCardBlur}
                          onBrandChange={handleCardBrandChange}
                          onCancel={isDebitCardExisting ? handleCardCancel : undefined}
                          onRevert={isDebitCardExisting ? handleCardRevert : undefined}
                          disabled={!values.isEditingDebitCard && isDebitCardExisting}
                          error={!!(get(errors, 'debitCard.cardNumber') || cardError)}
                          label="Debit Card Number"
                          placeholder=""
                          isRequired
                        />
                        {(get(errors, 'debitCard.cardNumber') || cardError) && (
                          <span className="error">{get(errors, 'debitCard.cardNumber') || cardError}</span>
                        )}
                        {!cardError && !get(errors, 'debitCard.cardNumber') && <small>Discover, Visa or Mastercard only</small>}
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <Input
                          name="debitCard.expirationDate"
                          label="Expiration Date"
                          onChange={handleInputChange}
                          value={get(values, 'debitCard.expirationDate') || ''}
                          isMasked={[/\d/, /\d/, '/', /\d/, /\d/]}
                          isRequired
                          hasError={!!get(errors, 'debitCard.expirationDate')}
                          errorMessage={get(errors, 'debitCard.expirationDate')}
                          placeHolder="MM/YY"
                          readOnly={!values.isEditingDebitCard && isDebitCardExisting}
                          inputmode="numeric"
                        />
                      </FormColumn>
                    </FormRow>
                  </>
                )
              }
              {hasError && (values.isSelfSubmitted === 1 || values.isSelfSubmitted === 2) && (
                <FormRow>
                  <FormColumn>
                    <div className="alert-container">
                      {errorMessage || 'The payment format used did not go through. Please try again or use another method.'}
                    </div>
                  </FormColumn>
                </FormRow>
              )}
              {get(values, 'avsFailed') && (
                <>
                  <FormRow>
                    <FormColumn>
                      <Input
                        label="Street Address"
                        name="owner.line1"
                        value={get(values, 'owner.line1')}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!get(errors, 'owner.line1')}
                        errorMessage={get(errors, 'owner.line1')}
                        onBlur={handleBlur}
                        className="streetAddress"
                      />
                      <VerifyButton
                        onClick={isVerified ? handleUndo : handleVerifyAddressClick}
                        isVerified={isVerified}
                        isLoading={isAddressLoading}
                      />
                      {isAddressPopupVisible && (
                        <Popup
                          handleAbort={handleAbort}
                          type={popupType}
                          data={{
                            address: get(values, 'owner.line1'),
                            city: get(values, 'owner.city'),
                            state: get(values, 'owner.state'),
                            zipcode: get(values, 'owner.zipcode'),
                          }}
                        />
                      )}
                    </FormColumn>
                  </FormRow>
                  <FormRow>
                    <FormColumn>
                      <Input
                        label="City"
                        name="owner.city"
                        value={get(values, 'owner.city')}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!get(errors, 'owner.city')}
                        errorMessage={get(errors, 'owner.city')}
                        onBlur={handleBlur}
                      />
                    </FormColumn>
                    <FormColumn>
                      <Select
                        name="owner.state"
                        data={states}
                        value={get(values, 'owner.state')}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        label="State"
                        isRequired
                        hasError={!!get(errors, 'owner.state')}
                        errorMessage={get(errors, 'owner.state')}
                      />
                    </FormColumn>
                    <FormColumn>
                      <Input
                        label="Zip"
                        isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                        name="owner.zipcode"
                        value={get(values, 'owner.zipcode')}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!get(errors, 'owner.zipcode')}
                        errorMessage={get(errors, 'owner.zipcode')}
                      />
                    </FormColumn>
                  </FormRow>
                </>
              )}
            </div>
          </div>
          <div className="content-section w-100">
            <hr className="separator pt-20" />
            <FormRow className="mb-25">
              <FormColumn>
                <div className="header">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 2v4" />
                    <path d="M16 2v4" />
                    <rect width="18" height="18" x="3" y="4" rx="2" />
                    <path d="M3 10h18" />
                  </svg>
                  <h2>Pick Payment Date</h2>
                </div>
                <p>Select a date which matches your payroll date</p>
              </FormColumn>
            </FormRow>
            <FormRow className="payroll-date">
              <FormColumn>
                <Input
                  label="First Payment Date"
                  name="firstPaymentDate"
                  value={values.firstPaymentDate ? `${values.firstPaymentDate.format('MM/DD/YYYY')}` : ''}
                  hasError={errors.firstPaymentDate}
                  onFocus={handleFocus}
                  isRequired
                  readOnly
                />
              </FormColumn>
            </FormRow>
          </div>
          <div className="content-section w-100">
            <hr className="separator pt-10" />
            <FormRow className="save-and-continue">
              <FormColumn>
                <Button
                  onClick={(e) => handleSubmitForm(false, e)}
                  className="btn btn-primary save-button"
                  title="Save And Continue"
                  isDisabled={isProgressVisible}
                />
              </FormColumn>
            </FormRow>
            <FormRow className="pb-25">
              <FormColumn>
                <p style={{ color: '#797a93', fontSize: 12 }}>
                  <em style={{ color: '#002147', fontStyle: 'normal' }}>
                    <b style={{ color: '#3989E3' }}>Please Note:</b>
                  </em>
                  &nbsp;
                  Enrolling in Auto-Pay is optional. The Bank&apos;s credit decision is not dependent on whether you enroll in Auto-Pay. You can change your method of payment at any time in the future.
                </p>
                <p style={{ color: '#797a93', fontSize: 12 }}>
                  * Your loan may have a No Interest on Principal Option Promotion included. This promotion can save you money if you pay off the principal amount of the loan in full within the Promotional Period (&quot;Promotional Period&quot;). During the Promotional Period you will be responsible for making all of your monthly payments and your loan will accrue interest on a monthly basis. If you pay off your loan within the Promotional Period, the monthly payments that you have made during this period, which includes accrued interest, will be deducted from the principal amount of the loan . Length of Promotional Periods vary, please review your loan agreement for full details.
                </p>
              </FormColumn>
            </FormRow>
          </div>
        </ContentWrapper>
      </Transition>
      {
        isProgressVisible && <Progress percent={uploadingPercentage} />
      }
      {isNoAutopayPopupVisible && (
        <Modal toggle={toggleNoAutopayPopup}>
          <div className="ach-modal-content">
            <div className="d-flex justify-content-end align-items-center close-link-container" onClick={toggleNoAutopayPopup}>
              <span className="close-link">Keep my current selection</span>
              <img src={XIcon} alt="x icon" width="10px" />
            </div>
            <h2 className="noautopay-modal-title">
              {noMatchingOffer
                ? 'Never Miss a Payment'
                : 'Don\'t Lose Your No Interest Promotion!*'}
            </h2>
            <h2 className="noautopay-modal-subtitle">
              {noMatchingOffer
                ? 'Turn on autopay and let your payments run on autopilot!'
                : 'Sign up for optional AutoPay today!'}
            </h2>
            <div className="d-flex justify-content-center icon-container">
              <img src={NoAchIcon} alt="no ach icon" />
            </div>
            <p className="text-center banner-description">
              We
              &nbsp;
              <b>recommend</b>
              &nbsp;
              using a debit card to automatically
              <br />
              make your monthly payments. it&apos;s free and easy to use!
            </p>

            <div className="d-flex justify-content-center mt-4 noautopay-btn-container">
              <Button
                onClick={handleUseDebitCard}
                className="btn btn-primary save-button"
                title="Yes - Use Debit Card"
              />
            </div>
            <div className="snack-warning">
              Note:
              &nbsp;
              <i>Using AutoPay saves you the hassle of calling in, or mailing a check to make a payment.</i>
            </div>
            {!noMatchingOffer && (
              <p className="noautopay-footer-disclosure">
                * Your loan may have a No Interest on Principal Option Promotion included. This promotion can save you money if you pay off the principal amount of the loan in full within the Promotional Period (&quot;Promotional Period&quot;). During the Promotional Period you will be responsible for making all of your monthly payments and your loan will accrue interest on a monthly basis. If you pay off your loan within the Promotional Period, the monthly payments that you have made during this period, which includes accrued interest, will be deducted from the principal amount of the loan . Length of Promotional Periods vary, please review your loan agreement for full details.
              </p>
            )}
          </div>
        </Modal>
      )}
      {/* <!-- End Disclaimer Modal --> */}
      {
        isGiactPopupVisible && (
          <Modal>
            <div className="card-content error-modal security-error">
              <img src={imgError} alt="" />
              <p>{giactMessage}</p>
              {giactPopupAttrs && giactPopupAttrs.allowRetry && (
                <div onClick={toggleGiactPopup} className="btn btn-red bottomMargin btn-no-autopay">TRY AGAIN </div>
              )}
              {giactPopupAttrs && giactPopupAttrs.allowToProceedWithoutAutopay && (
                <div onClick={(e) => handleSubmitForm(true, e)} className={`${!giactPopupAttrs.allowRetry ? 'btn btn-red' : 'btn'} btn-no-autopay`}>
                  Proceed without signing up with autopay
                </div>
              )}
            </div>
          </Modal>
        )
      }
      {
        isDatePickVisible && (
          <Modal>
            <Datetime
              input={false}
              className="dateWrapper"
              initialValue={values.firstPaymentDate}
              value={firstPaymentDate}
              displayTimeZone="America/Los_Angeles"
              isValidDate={(current) => (
                current.isAfter(Datetime.moment().add(36, 'd'))
                && current.isBefore(Datetime.moment().add(47, 'd'))
              )}
              onChange={handleDateChange}
              dateFormat="MM/DD/YYYY"
              timeFormat={false}
            />
            <div style={{ padding: 10, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                onClick={handleCloseModal}
                className="btn btn-md btn-blue-outline"
                title="Close"
              />
              <Button
                onClick={handleConfirmDate}
                className="btn btn-md btn-blue"
                title="Confirm"
              />
            </div>
          </Modal>
        )
      }
    </div>
  );
};

LuwaACH.propTypes = {
  navigate: PropTypes.func.isRequired,
  checkinData: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
  newNextAction: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  validateBankRoutingNumberAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
});

const mapDispatchToProps = {
  newNextAction,
  validateBankRoutingNumberAction,
};

export default Validator(schema)(connect(mapStateToProps, mapDispatchToProps)(LuwaACH));
